import {
  getVariablesForPair,
  type TemplateLibraryEntry,
  type TemplatePair,
} from '@fluenceur/types';

/**
 * Pair-keyed template selection helpers shared by every send flow (invitation
 * modal and the email composers). These are pure functions over the template
 * library; the flow-specific glue (which store the body lands in) lives next to
 * each caller.
 */

/** Keep only the entries that belong to the given (channel, context) pair. */
export const entriesForPair = (
  entries: readonly TemplateLibraryEntry[],
  pair: TemplatePair
): TemplateLibraryEntry[] =>
  entries.filter((entry) => entry.channel === pair.channel && entry.context === pair.context);

/**
 * Resolve which template should be pre-selected for a pair.
 *
 * Precedence:
 *  1. the brand template flagged `isDefault`
 *  2. the system default (read-only baseline shipped with the platform)
 *  3. the first available entry
 *
 * Returns `null` only when no entry exists for the pair, in which case the
 * caller keeps the legacy backend-prepared fallback.
 */
export const pickDefaultEntry = (
  entries: readonly TemplateLibraryEntry[]
): TemplateLibraryEntry | null => {
  if (entries.length === 0) {
    return null;
  }
  return (
    entries.find((entry) => entry.isDefault) ??
    entries.find((entry) => entry.isSystemDefault) ??
    entries[0]
  );
};

/** A Fluenceur (system) template rather than one the brand owns. */
export const isFluenceurTemplate = (entry: TemplateLibraryEntry): boolean =>
  entry.isSystem || entry.isSystemDefault;

/**
 * A brand template carries an attributable id; a Fluenceur template does not
 * (the backend only attributes brand-owned ids and persists `templateId = null`
 * otherwise). Returns the id to send for an entry, or `null` for a Fluenceur
 * template.
 */
export const attributableTemplateId = (
  entry: TemplateLibraryEntry | null | undefined
): string | null => {
  if (!entry) {
    return null;
  }
  return isFluenceurTemplate(entry) ? null : entry.id;
};

/**
 * Carry over the values the user already typed for variables that exist in
 * BOTH the previous and next (channel, context) pairs. Values keyed by a
 * variable absent from the next pair are dropped; variables introduced by the
 * next pair start empty. The previous values object is never mutated.
 *
 * Used when switching the selected template so manual edits to shared variables
 * (e.g. `first_name`, `brand_name`) survive the body reload.
 */
export const carryOverVariableValues = (
  previousValues: Readonly<Record<string, string>>,
  previousPair: TemplatePair,
  nextPair: TemplatePair
): Record<string, string> => {
  const nextKeys = new Set(
    getVariablesForPair(nextPair.channel, nextPair.context).map((variable) => variable.key)
  );
  const previousKeys = new Set(
    getVariablesForPair(previousPair.channel, previousPair.context).map((variable) => variable.key)
  );

  const carried: Record<string, string> = {};
  for (const [key, value] of Object.entries(previousValues)) {
    if (nextKeys.has(key) && previousKeys.has(key)) {
      carried[key] = value;
    }
  }
  return carried;
};
